import {
  AlertCircle,
  CheckCircle2,
  Download,
  RotateCcw,
  Upload,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { FileInput } from "components/ui/file-input";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";

import { formatClock, formatEnd } from "../../event-experience/agenda-schedule";
import type {
  AgendaItem,
  Speaker,
  Track,
  Venue,
} from "../../event-experience/schemas";
import { timezonePlace } from "../../events/timezones";
import type { AttendeeRecord } from "../use-attendee-records";
import { outsideEventDates } from "../event-dates-fit";
import type { EventSettings } from "../schemas";
import { OutsideDatesNote } from "./OutsideDatesNote";
import { WarningNote } from "./WarningNote";

import {
  AGENDA_COLUMNS,
  AGENDA_TEMPLATE_FILE_NAME,
  agendaTemplateCsv,
  type AgendaImportContext,
  type AgendaMapping,
  type AgendaRowCheck,
  type AgendaRowValues,
  checkAgendaRow,
  checkAgendaRows,
  collectUnknownSpeakers,
  guessAgendaMapping,
  mappingProblem,
  mapRowValues,
  MAX_AGENDA_IMPORT_ROWS,
  presenterCandidates,
} from "./agenda-import";
import {
  AGENDA_IMPORT_BATCH_SIZE,
  agendaItemExists,
  newAgendaItemId,
  writeAgendaBatch,
} from "./agenda-import-writes";
import {
  decodeImportCsv,
  type ImportCsvRow,
  MAX_IMPORT_BYTES,
  parseImportCsv,
  readFileBytes,
  TOO_LARGE_CSV,
  UNDECODABLE_CSV,
} from "./import-csv";

/** Issue cards shown before "Show more", so a broken 1000-row file stays usable. */
const ISSUES_SHOWN = 50;
/** 44px tall on a phone, the smallest target a thumb hits reliably. */
const PHONE_TAP = "h-11 min-w-11 sm:h-9 sm:min-w-0";
/** Ready rows listed in the preview. The count above them covers the rest. */
const READY_SHOWN = 100;

interface RowState {
  rowNumber: number;
  /** The file's cells by header, as parsed. */
  cells: Record<string, string>;
  overflow: boolean;
  /** Set by Fix, and then used instead of the file's cells under the mapping. */
  edited: AgendaRowValues | null;
  dismissed: boolean;
  /** The organiser said to import this one although it repeats a session. */
  keepDuplicate: boolean;
  /** Minted once, so a retry writes the same document rather than a second one. */
  id: string;
  write: "idle" | "written" | "failed";
  writeError: string | null;
  /**
   * Speakers the row named that the event had no one for, kept from the moment
   * it was written: the session is on the agenda without them, and the list
   * of names to add must outlive the check, which skips written rows.
   */
  unknownSpeakers: string[];
}

/**
 * The agenda import: a CSV in, agenda items out, with a look at every row
 * before anything is written.
 *
 * It runs in the browser and writes with batched client writes, not through a
 * command and a Cloud Run job as the attendee import does. The attendee import
 * needs the job because a row there becomes several documents across the
 * roster, the email index and registrations, which only the Admin SDK may
 * write. An agenda row becomes one document the organiser may already create
 * from the agenda form, so the same rules check every imported session, and
 * the same triggers derive `dayKey` and the presenter projection. What the
 * job would add is durability for long runs, and a file is capped at
 * `MAX_AGENDA_IMPORT_ROWS`, a hundred batches of `AGENDA_IMPORT_BATCH_SIZE`.
 *
 * Re-importing appends. There is no id column to update sessions by, because
 * a session's times are command-owned (RESCHEDULE_AGENDA) and an update by
 * import would need that command as well as the direct write. A row whose
 * title and start match a session already on the agenda, or an earlier row,
 * is held back as a duplicate until the organiser imports it anyway, fixes it
 * or leaves it out.
 */
export function AgendaImportPanel({
  eventId,
  timeZone,
  rawTimeZone,
  tracks,
  speakers,
  attendees,
  venues,
  existing,
  uid,
  event,
  reloadEvent,
  loadError,
  onClose,
  onImported,
}: {
  eventId: string;
  /** The event's zone when this browser can resolve it. */
  timeZone: string | undefined;
  /** The stored zone, for saying which one is unusable. */
  rawTimeZone: string | undefined;
  tracks: Track[] | null;
  speakers: Speaker[] | null;
  attendees: AttendeeRecord[] | null;
  venues: Venue[] | null;
  existing: AgendaItem[];
  /**
   * The event's dates, and a way to read the event again after they are
   * changed. Without them the panel cannot say that the file is outside the
   * event's dates.
   */
  uid?: string;
  event?: Pick<EventSettings, "startDate" | "endDate" | "dayTitles">;
  reloadEvent?: () => void;
  /** Why one of the lists above could not be read, if one could not. */
  loadError: string | null;
  onClose: () => void;
  onImported: () => void;
}) {
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<AgendaMapping>({});
  const [rows, setRows] = useState<RowState[]>([]);
  const [fixing, setFixing] = useState<number | null>(null);
  const [writing, setWriting] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [issuesShown, setIssuesShown] = useState(ISSUES_SHOWN);

  const loaded =
    tracks !== null &&
    speakers !== null &&
    attendees !== null &&
    venues !== null;

  const context = useMemo<AgendaImportContext | null>(
    () =>
      timeZone && loaded
        ? {
            timeZone,
            tracks,
            venues,
            people: presenterCandidates(attendees, speakers),
            existing,
          }
        : null,
    [timeZone, loaded, tracks, venues, attendees, speakers, existing],
  );

  const problemWithMapping =
    headers.length > 0 ? mappingProblem(mapping) : null;

  const checks = useMemo(() => {
    if (!context || problemWithMapping !== null)
      return new Map<number, AgendaRowCheck>();
    return checkRows(rows, mapping, context);
  }, [context, problemWithMapping, rows, mapping]);

  const groups = useMemo(() => {
    const ready: RowState[] = [];
    const attention: RowState[] = [];
    const dismissed: RowState[] = [];
    const written: RowState[] = [];
    for (const row of rows) {
      if (row.write === "written") written.push(row);
      else if (row.dismissed) dismissed.push(row);
      else if (isReady(row, checks.get(row.rowNumber))) ready.push(row);
      else attention.push(row);
    }
    return { ready, attention, dismissed, written };
  }, [rows, checks]);

  // The rows about to be imported that fall on days the event's dates do not
  // include. The sessions already on the agenda are not counted, but the dates
  // on offer cover them too, so setting them does not strand those.
  const outsideDates = useMemo(() => {
    if (!event) return null;
    const starts = groups.ready.flatMap((row) => {
      const draft = checks.get(row.rowNumber)?.draft;
      return draft ? [draft.startsAt] : [];
    });
    return outsideEventDates(
      starts,
      event,
      timeZone,
      existing.map((item) => item.startsAt),
    );
  }, [event, groups.ready, checks, timeZone, existing]);

  // Speakers the file names that the event has no one for, once each with the
  // rows they are in. Rows that will be or were imported count; a row held
  // back for another reason, or left out, does not yet.
  const unknownSpeakers = useMemo(
    () =>
      collectUnknownSpeakers(
        rows.flatMap((row) => {
          if (row.dismissed) return [];
          if (row.write === "written")
            return [
              {
                rowNumber: row.rowNumber,
                unknownSpeakers: row.unknownSpeakers,
              },
            ];
          const check = checks.get(row.rowNumber);
          return check && isReady(row, check)
            ? [
                {
                  rowNumber: row.rowNumber,
                  unknownSpeakers: check.unknownSpeakers,
                },
              ]
            : [];
        }),
      ),
    [rows, checks],
  );

  // A reload while batches are going in stops the run part-way. The rows
  // already written stay, and the rest are lost with the page.
  useEffect(() => {
    if (!writing) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [writing]);

  async function chooseFile(file: File | null) {
    setFileProblem(null);
    setHeaders([]);
    setMapping({});
    setRows([]);
    setIssuesShown(ISSUES_SHOWN);
    if (!file) return;
    if (file.size > MAX_IMPORT_BYTES) {
      setFileProblem(TOO_LARGE_CSV);
      return;
    }
    const text = decodeImportCsv(await readFileBytes(file));
    if (text === null) {
      setFileProblem(UNDECODABLE_CSV);
      return;
    }
    const parsed = parseImportCsv(text);
    if (!parsed.ok) {
      setFileProblem(parsed.problem);
      return;
    }
    if (parsed.rows.length === 0) {
      setFileProblem("The file has a header row and no sessions under it.");
      return;
    }
    if (parsed.rows.length > MAX_AGENDA_IMPORT_ROWS) {
      setFileProblem(
        `The file has ${parsed.rows.length} rows. Import at most ${MAX_AGENDA_IMPORT_ROWS} at a time, by splitting it into smaller files.`,
      );
      return;
    }
    setHeaders(parsed.headers);
    setMapping(guessAgendaMapping(parsed.headers));
    setRows(parsed.rows.map((row) => initialRow(row, eventId)));
  }

  function updateRow(rowNumber: number, change: Partial<RowState>) {
    setRows((current) =>
      current.map((row) =>
        row.rowNumber === rowNumber ? { ...row, ...change } : row,
      ),
    );
  }

  /**
   * Write rows in batches, one batch at a time, marking each row as its batch
   * lands or is refused. A refused batch does not stop the run: its rows go
   * to the list with Retry, and the next batch is tried.
   */
  async function write(
    targets: RowState[],
    checked: Map<number, AgendaRowCheck> = checks,
  ) {
    const items = targets.flatMap((row) => {
      const draft = checked.get(row.rowNumber)?.draft;
      return draft ? [{ row, draft }] : [];
    });
    if (items.length === 0) return;
    setWriting({ done: 0, total: items.length });
    let done = 0;
    let wroteAny = false;
    for (let at = 0; at < items.length; at += AGENDA_IMPORT_BATCH_SIZE) {
      const slice = items.slice(at, at + AGENDA_IMPORT_BATCH_SIZE);
      const outcome = await writeAgendaBatch(
        eventId,
        slice.map(({ row, draft }) => ({ id: row.id, draft })),
      );
      const numbers = new Set(slice.map(({ row }) => row.rowNumber));
      const unknown = new Map(
        slice.map(({ row }) => [
          row.rowNumber,
          checked.get(row.rowNumber)?.unknownSpeakers ?? [],
        ]),
      );
      setRows((current) =>
        current.map((row) =>
          numbers.has(row.rowNumber)
            ? outcome.ok
              ? {
                  ...row,
                  write: "written",
                  writeError: null,
                  unknownSpeakers: unknown.get(row.rowNumber) ?? [],
                }
              : { ...row, write: "failed", writeError: outcome.error }
            : row,
        ),
      );
      if (outcome.ok) wroteAny = true;
      done += slice.length;
      setWriting({ done, total: items.length });
    }
    setWriting(null);
    if (wroteAny) onImported();
  }

  async function retry(targets: RowState[]) {
    // A batch whose answer never arrived may have landed. Those rows are
    // done; writing them again would be refused as an update.
    const landed = new Set<number>();
    for (const row of targets) {
      if (await agendaItemExists(eventId, row.id).catch(() => false))
        landed.add(row.rowNumber);
    }
    if (landed.size > 0) onImported();
    // The duplicate check again, over the rows as they are now: a copy of a
    // failed row may have landed from another batch since it was checked.
    // A row that now repeats a session goes back to the list as a duplicate,
    // with Import anyway, and is not written.
    const now = rows.map((row) =>
      landed.has(row.rowNumber)
        ? {
            ...row,
            write: "written" as const,
            writeError: null,
            unknownSpeakers: checks.get(row.rowNumber)?.unknownSpeakers ?? [],
          }
        : row,
    );
    const checked = context
      ? checkRows(now, mapping, context)
      : new Map<number, AgendaRowCheck>();
    const again = targets.filter((row) => !landed.has(row.rowNumber));
    const clear = again.filter((row) =>
      isReady({ ...row, write: "idle" }, checked.get(row.rowNumber)),
    );
    const held = new Set(
      again.filter((row) => !clear.includes(row)).map((row) => row.rowNumber),
    );
    setRows((current) =>
      current.map((row) =>
        landed.has(row.rowNumber)
          ? {
              ...row,
              write: "written",
              writeError: null,
              unknownSpeakers: checks.get(row.rowNumber)?.unknownSpeakers ?? [],
            }
          : held.has(row.rowNumber)
            ? { ...row, write: "idle", writeError: null }
            : row,
      ),
    );
    await write(clear, checked);
  }

  if (!timeZone) {
    return (
      <Shell onClose={onClose}>
        <p
          className="flex items-start gap-2 text-sm text-destructive-text"
          data-testid="agenda-import-no-zone"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {rawTimeZone
            ? `This event's time zone, ${rawTimeZone}, is not one this browser knows.`
            : "This event has no time zone."}{" "}
          Choose one on the Settings tab first, so the times in the file are
          read on the event&apos;s clock.
        </p>
      </Shell>
    );
  }

  const busy = writing !== null;
  const failed = groups.attention.filter((row) => row.write === "failed");
  const fixingRow = rows.find((row) => row.rowNumber === fixing) ?? null;

  return (
    <Shell onClose={onClose} closeDisabled={busy}>
      <div className="space-y-1.5">
        <Label htmlFor="agenda-import-file">CSV file</Label>
        <FileInput
          id="agenda-import-file"
          accept=".csv,text/csv"
          testId="agenda-import-file"
          disabled={busy}
          onChange={(event) => void chooseFile(event.target.files?.[0] ?? null)}
        />
        <p className="text-xs text-muted-foreground">
          Up to {MAX_AGENDA_IMPORT_ROWS} sessions, saved as CSV UTF-8 with
          commas or semicolons between columns, and a header row first. Title,
          start time and end time are required, and every row needs a date,
          either in a Date column or in front of its start time. Dates are
          2026-10-01 or 01.10.2026. Times are read on {timezonePlace(timeZone)}{" "}
          time ({timeZone}), the event&apos;s time zone.
        </p>
        <p className="text-xs text-muted-foreground">
          Tracks, speakers and venues are matched by name to the ones this event
          already has, and speakers by email too. Separate several with a
          semicolon. Nothing new is created. A track or venue that is missing
          holds its row back, so add it on its tab first. A speaker that is
          missing is left off the session, and listed after the check so you can
          add them.
        </p>
        <a
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(agendaTemplateCsv())}`}
          download={AGENDA_TEMPLATE_FILE_NAME}
          className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-lit underline-offset-4 hover:underline"
          data-testid="agenda-import-template"
        >
          <Download className="h-4 w-4" />
          Download the template
        </a>
      </div>

      {fileProblem && (
        <p
          className="text-sm text-destructive-text"
          data-testid="agenda-import-error"
        >
          {fileProblem}
        </p>
      )}

      {headers.length > 0 && (
        <div className="space-y-3" data-testid="agenda-import-mapping">
          <p className="text-sm font-medium text-foreground">
            Match your columns
          </p>
          {AGENDA_COLUMNS.map((column) => (
            <div
              key={column.key}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
            >
              <Label htmlFor={`agenda-map-${column.key}`} className="shrink-0">
                {column.label}
                {column.required ? " *" : ""}
              </Label>
              <select
                id={`agenda-map-${column.key}`}
                data-testid={`agenda-map-${column.key}`}
                value={mapping[column.key] ?? ""}
                disabled={busy || groups.written.length > 0}
                onChange={(event) =>
                  setMapping({ ...mapping, [column.key]: event.target.value })
                }
                className="h-11 w-full min-w-0 rounded-md border border-input bg-background px-2 text-base text-foreground sm:w-64 sm:text-sm"
              >
                <option value="">Not imported</option>
                {headers.map((header) => (
                  <option key={header} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {problemWithMapping && (
            <p
              className="text-sm text-destructive-text"
              data-testid="agenda-import-mapping-error"
            >
              {problemWithMapping}
            </p>
          )}
        </div>
      )}

      {loadError && (
        <p
          className="text-sm text-destructive-text"
          data-testid="agenda-import-load-error"
        >
          The rows cannot be checked, because a list they are matched against
          did not load: {loadError}
        </p>
      )}

      {headers.length > 0 && !loaded && !loadError && (
        <p
          className="text-sm text-muted-foreground"
          data-testid="agenda-import-loading"
        >
          Loading the tracks, speakers and venues to match the rows against…
        </p>
      )}

      {headers.length > 0 && context && problemWithMapping === null && (
        <div className="space-y-4">
          <p
            className="text-sm text-foreground"
            data-testid="agenda-import-summary"
          >
            {summary(groups)}
          </p>

          {writing && (
            <p
              role="status"
              className="text-sm text-muted-foreground"
              data-testid="agenda-import-progress"
            >
              Importing… {writing.done} of {writing.total} written.
            </p>
          )}

          {event && reloadEvent && (
            <OutsideDatesNote
              eventId={eventId}
              uid={uid}
              timeZone={timeZone}
              event={event}
              reload={reloadEvent}
              info={outsideDates}
              subject="file"
              testId="agenda-import-outside-dates"
            />
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              variant="cta"
              className="h-11 sm:h-10"
              disabled={busy || groups.ready.length === 0}
              data-testid="agenda-import-start"
              onClick={() => void write(groups.ready)}
            >
              <Upload className="h-4 w-4" />
              Import {groups.ready.length}{" "}
              {groups.ready.length === 1 ? "session" : "sessions"}
            </Button>
            {failed.length > 0 && (
              <Button
                variant="outline"
                className="h-11 sm:h-10"
                disabled={busy}
                data-testid="agenda-import-retry-all"
                onClick={() => void retry(failed)}
              >
                <RotateCcw className="h-4 w-4" />
                Retry {failed.length} failed
              </Button>
            )}
          </div>

          {groups.written.length > 0 && !busy && (
            <p
              className="flex items-center gap-1.5 text-sm text-foreground"
              data-testid="agenda-import-done"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0 text-lit" />
              {groups.written.length}{" "}
              {groups.written.length === 1 ? "session is" : "sessions are"} on
              the agenda now.
            </p>
          )}

          {unknownSpeakers.length > 0 && (
            <WarningNote testId="agenda-import-unknown-speakers">
              <span className="block font-medium">
                {unknownSpeakers.length === 1
                  ? "1 speaker was not found"
                  : `${unknownSpeakers.length} speakers were not found`}
              </span>
              <span className="mt-1 block">
                Sessions that name them are imported without them. Add them on
                the Speakers tab, then add them to these sessions on the Agenda
                tab.
              </span>
              <ul className="mt-2 space-y-1">
                {unknownSpeakers.map((speaker, index) => (
                  <li
                    key={speaker.name}
                    className="break-words"
                    data-testid={`agenda-import-unknown-speaker-${index}`}
                  >
                    “{speaker.name}”,{" "}
                    {speaker.rows.length === 1 ? "row" : "rows"}{" "}
                    {speaker.rows.join(", ")}
                  </li>
                ))}
              </ul>
            </WarningNote>
          )}

          {groups.attention.length > 0 && (
            <div className="space-y-2" data-testid="agenda-import-issues">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">
                  Rows that need attention
                </p>
                <Button
                  size="sm"
                  className={PHONE_TAP}
                  variant="outline"
                  disabled={busy}
                  data-testid="agenda-import-dismiss-all"
                  onClick={() => {
                    const numbers = new Set(
                      groups.attention.map((row) => row.rowNumber),
                    );
                    setRows((current) =>
                      current.map((row) =>
                        numbers.has(row.rowNumber)
                          ? { ...row, dismissed: true }
                          : row,
                      ),
                    );
                  }}
                >
                  Leave out all {groups.attention.length}
                </Button>
              </div>
              {groups.attention.slice(0, issuesShown).map((row) => (
                <IssueCard
                  key={row.rowNumber}
                  row={row}
                  check={checks.get(row.rowNumber)}
                  title={(row.edited ?? mapRowValues(row.cells, mapping)).title}
                  busy={busy}
                  onFix={() => setFixing(row.rowNumber)}
                  onKeep={() =>
                    updateRow(row.rowNumber, { keepDuplicate: true })
                  }
                  onRetry={() => void retry([row])}
                  onDismiss={() =>
                    updateRow(row.rowNumber, { dismissed: true })
                  }
                />
              ))}
              {groups.attention.length > issuesShown && (
                <Button
                  size="sm"
                  className={PHONE_TAP}
                  variant="ghost"
                  data-testid="agenda-import-more-issues"
                  onClick={() => setIssuesShown((n) => n + ISSUES_SHOWN)}
                >
                  Show{" "}
                  {Math.min(
                    ISSUES_SHOWN,
                    groups.attention.length - issuesShown,
                  )}{" "}
                  more
                </Button>
              )}
            </div>
          )}

          {groups.ready.length > 0 && (
            <div className="space-y-2" data-testid="agenda-import-ready">
              <p className="text-sm font-medium text-foreground">
                Ready to import
              </p>
              <ul className="divide-y divide-border rounded-md border border-border">
                {groups.ready.slice(0, READY_SHOWN).map((row) => {
                  const check = checks.get(row.rowNumber);
                  const draft = check?.draft;
                  if (!draft) return null;
                  return (
                    <li
                      key={row.rowNumber}
                      className="space-y-0.5 px-3 py-2 text-sm"
                      data-testid={`agenda-import-ready-${row.rowNumber}`}
                    >
                      <p className="break-words font-medium text-foreground">
                        {draft.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Row {row.rowNumber} ·{" "}
                        {formatWhen(draft.startsAt, draft.endsAt, timeZone)}
                        {draft.roomName ? ` · ${draft.roomName}` : ""}
                        {draft.location ? ` · ${draft.location}` : ""}
                      </p>
                      {check.notes.map((note) => (
                        <p key={note} className="text-xs text-muted-foreground">
                          {note}
                        </p>
                      ))}
                      {check.duplicate && row.keepDuplicate && (
                        <p className="text-xs text-muted-foreground">
                          Imported although it repeats a session.{" "}
                          {check.duplicate}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
              {groups.ready.length > READY_SHOWN && (
                <p className="text-xs text-muted-foreground">
                  And {groups.ready.length - READY_SHOWN} more.
                </p>
              )}
            </div>
          )}

          {groups.dismissed.length > 0 && (
            <details data-testid="agenda-import-dismissed">
              <summary className="flex min-h-11 cursor-pointer items-center text-sm text-muted-foreground">
                {groups.dismissed.length}{" "}
                {groups.dismissed.length === 1 ? "row" : "rows"} left out
              </summary>
              <ul className="space-y-1">
                {groups.dismissed.map((row) => (
                  <li
                    key={row.rowNumber}
                    className="flex items-center justify-between gap-2 text-sm"
                  >
                    <span className="min-w-0 break-words text-muted-foreground">
                      Row {row.rowNumber}:{" "}
                      {(row.edited ?? mapRowValues(row.cells, mapping)).title ||
                        "(no title)"}
                    </span>
                    <Button
                      size="sm"
                      className={PHONE_TAP}
                      variant="ghost"
                      disabled={busy}
                      data-testid={`agenda-import-restore-${row.rowNumber}`}
                      onClick={() =>
                        updateRow(row.rowNumber, { dismissed: false })
                      }
                    >
                      Put back
                    </Button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {fixingRow && context && (
        <FixRowDialog
          key={fixingRow.rowNumber}
          rowNumber={fixingRow.rowNumber}
          values={fixingRow.edited ?? mapRowValues(fixingRow.cells, mapping)}
          context={context}
          onClose={() => setFixing(null)}
          onSave={(values) => {
            updateRow(fixingRow.rowNumber, {
              edited: values,
              // A changed row is a new row: its duplicate decision and its
              // failed write were about the old values.
              keepDuplicate: false,
              write: "idle",
              writeError: null,
            });
            setFixing(null);
          }}
        />
      )}
    </Shell>
  );
}

function initialRow(row: ImportCsvRow, eventId: string): RowState {
  return {
    rowNumber: row.rowNumber,
    cells: row.values,
    overflow: row.overflow,
    edited: null,
    dismissed: false,
    keepDuplicate: false,
    id: newAgendaItemId(eventId),
    write: "idle",
    writeError: null,
    unknownSpeakers: [],
  };
}

/** Every row checked under this mapping, written rows compared but not checked. */
function checkRows(
  rows: RowState[],
  mapping: AgendaMapping,
  context: AgendaImportContext,
): Map<number, AgendaRowCheck> {
  const written = rows.filter((row) => row.write === "written");
  return checkAgendaRows(
    rows.map((row) => ({
      rowNumber: row.rowNumber,
      values: row.edited ?? mapRowValues(row.cells, mapping),
      overflow: row.edited ? false : row.overflow,
    })),
    context,
    new Set(
      rows
        .filter((row) => row.dismissed || row.write === "written")
        .map((row) => row.rowNumber),
    ),
    new Set(written.map((row) => row.id)),
    new Set(written.map((row) => row.rowNumber)),
  );
}

function isReady(row: RowState, check: AgendaRowCheck | undefined): boolean {
  if (!check?.draft || row.write === "failed") return false;
  return check.duplicate === null || row.keepDuplicate;
}

function summary(groups: {
  ready: RowState[];
  attention: RowState[];
  dismissed: RowState[];
  written: RowState[];
}): string {
  const parts = [
    `${groups.ready.length} ready`,
    `${groups.attention.length} need attention`,
  ];
  if (groups.dismissed.length > 0)
    parts.push(`${groups.dismissed.length} left out`);
  if (groups.written.length > 0)
    parts.push(`${groups.written.length} imported`);
  return `${parts.join(", ")}.`;
}

function formatWhen(start: Date, end: Date, timeZone: string): string {
  const day = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(start);
  const until = formatEnd(
    { id: "", title: "", startsAt: start, endsAt: end },
    timeZone,
  );
  const later =
    until && until.daysLater > 0
      ? ` (+${until.daysLater} ${until.daysLater === 1 ? "day" : "days"})`
      : "";
  return `${day}, ${formatClock(start, timeZone)} to ${until?.clock ?? ""}${later}`;
}

function Shell({
  children,
  onClose,
  closeDisabled = false,
}: {
  children: ReactNode;
  onClose: () => void;
  closeDisabled?: boolean;
}) {
  return (
    <Card data-testid="agenda-import" className="border-border shadow-none">
      <CardContent className="space-y-4 pt-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">
              Import the agenda from a CSV file
            </h2>
            <p className="text-sm text-muted-foreground">
              Every row is checked before anything is written.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 sm:h-10 sm:w-10"
            aria-label="Close the import"
            disabled={closeDisabled}
            data-testid="agenda-import-close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

function IssueCard({
  row,
  check,
  title,
  busy,
  onFix,
  onKeep,
  onRetry,
  onDismiss,
}: {
  row: RowState;
  check: AgendaRowCheck | undefined;
  title: string;
  busy: boolean;
  onFix: () => void;
  onKeep: () => void;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  const failed = row.write === "failed";
  const reasons = failed
    ? [`The write was refused: ${row.writeError ?? "unknown error"}`]
    : [
        ...(check?.problems ?? []),
        ...(check?.duplicate ? [check.duplicate] : []),
      ];
  const onlyDuplicate =
    !failed && check?.draft !== null && check?.duplicate !== null;

  return (
    <div
      className="space-y-2 rounded-md border border-border p-3"
      data-testid={`agenda-import-issue-${row.rowNumber}`}
    >
      <p className="break-words text-sm font-medium text-foreground">
        Row {row.rowNumber}: {title || "(no title)"}
      </p>
      <ul className="space-y-1">
        {reasons.map((reason) => (
          <li
            key={reason}
            className="flex items-start gap-1.5 text-sm text-muted-foreground"
          >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive-text" />
            <span className="min-w-0 break-words">{reason}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {failed && (
          <Button
            size="sm"
            className={PHONE_TAP}
            variant="outline"
            disabled={busy}
            data-testid={`agenda-import-retry-${row.rowNumber}`}
            onClick={onRetry}
          >
            Retry
          </Button>
        )}
        <Button
          size="sm"
          className={PHONE_TAP}
          variant="outline"
          disabled={busy}
          data-testid={`agenda-import-fix-${row.rowNumber}`}
          onClick={onFix}
        >
          Fix
        </Button>
        {onlyDuplicate && (
          <Button
            size="sm"
            className={PHONE_TAP}
            variant="outline"
            disabled={busy}
            data-testid={`agenda-import-keep-${row.rowNumber}`}
            onClick={onKeep}
          >
            Import anyway
          </Button>
        )}
        <Button
          size="sm"
          className={PHONE_TAP}
          variant="ghost"
          disabled={busy}
          data-testid={`agenda-import-dismiss-${row.rowNumber}`}
          onClick={onDismiss}
        >
          Leave out
        </Button>
      </div>
    </div>
  );
}

/**
 * Correct one row in place, checked as it is typed, with the same rules as
 * the file. Saving puts the row back through the whole check, duplicates
 * included.
 */
function FixRowDialog({
  rowNumber,
  values: initial,
  context,
  onClose,
  onSave,
}: {
  rowNumber: number;
  values: AgendaRowValues;
  context: AgendaImportContext;
  onClose: () => void;
  onSave: (values: AgendaRowValues) => void;
}) {
  const [values, setValues] = useState(initial);
  const check = useMemo(
    () => checkAgendaRow(values, context),
    [values, context],
  );

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fix row {rowNumber}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
          {AGENDA_COLUMNS.map((column) => (
            <div key={column.key} className="space-y-1.5">
              <Label htmlFor={`agenda-fix-${column.key}`}>
                {column.label}
                {column.key === "title" ? " *" : ""}
              </Label>
              {column.key === "description" ||
              column.key === "locationNotes" ? (
                <Textarea
                  id={`agenda-fix-${column.key}`}
                  data-testid={`agenda-fix-${column.key}`}
                  rows={3}
                  value={values[column.key]}
                  onChange={(event) =>
                    setValues({ ...values, [column.key]: event.target.value })
                  }
                />
              ) : (
                <Input
                  id={`agenda-fix-${column.key}`}
                  testId={`agenda-fix-${column.key}`}
                  value={values[column.key]}
                  onChange={(event) =>
                    setValues({ ...values, [column.key]: event.target.value })
                  }
                />
              )}
            </div>
          ))}
        </div>
        {check.problems.length > 0 ? (
          <ul className="space-y-1" data-testid="agenda-fix-problems">
            {check.problems.map((problem) => (
              <li key={problem} className="text-sm text-destructive-text">
                {problem}
              </li>
            ))}
          </ul>
        ) : (
          <p
            className="text-sm text-muted-foreground"
            data-testid="agenda-fix-ok"
          >
            This row can be imported.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" className="h-11 sm:h-10" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="cta"
            className="h-11 sm:h-10"
            data-testid="agenda-fix-save"
            onClick={() => onSave(values)}
          >
            Save row
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
